/**
 * Adobe "Mathematical Pi" / "Universal" glyph-name -> Unicode table.
 *
 * Some older typeset PDFs (Berkeley/Futura/Mathematical-Pi journal templates
 * are the common offender) embed math symbols from fonts that carry NO
 * ToUnicode table and name their glyphs with Adobe's "H#####" scheme
 * (e.g. /H11350 instead of /greaterequal). pdf.js cannot resolve that naming
 * dialect, so it falls back to the raw glyph slot - which lands in the control
 * range (U+0001, U+0002, ...) and the text-selection sanitizer then treats it
 * as an "invalid" character and silently drops it.
 *
 * The glyph NAME, however, deterministically identifies the symbol. This table
 * maps the H-codes back to their real Unicode characters so the symbol can be
 * recovered accurately (not guessed) at text-extraction time.
 *
 * Every entry below was confirmed against rendered in-document context (the
 * glyph drawn next to text whose meaning is unambiguous), NOT inferred from a
 * code point. Add new entries only once you have the same proof - any glyph
 * name absent here falls through to the editable backstop in the sanitization
 * dialog, so an unknown symbol is surfaced to the user, never mis-converted.
 */

// glyph name (without the leading "/") -> the Unicode character it denotes.
// Where a clean ">=" style ASCII form exists, we still emit the true Unicode
// symbol (e.g. U+2265) so the existing normalizationMap can offer the ASCII
// conversion downstream - identical to how a well-behaved PDF already behaves.
// The expansion below the first block was verified by rendering each glyph from
// the corpus into a contact sheet and reading it, cross-checked against the
// surrounding text - same proven-only discipline, no guessing. Multiple H-codes
// map to one symbol because the Mathematical-Pi / Universal families use
// several code points for visually-equivalent glyphs.
const MATH_PI_GLYPH_MAP = {
	// --- relations ---
	H11350: "≥", // >=  greater than or equal     "BMI >=30", "achieved >=10% weight"
	H33356: "≥", //     "large doses (>=250 mg)"
	H11349: "≤", // <=  less than or equal        "<=50 kg/m^2"
	H11022: ">", // >   greater than                ">102 cm", ">160/95 mmHg"
	H11021: "<", // <   less than                   "P <0.01", "<10% of subjects"
	H11005: "=", // =   equals                       "n = 270", "n = 146"
	H11549: "=", //     "N = 349"
	H11505: "=", //     "P = 0.12"
	// --- operators ---
	H11006: "±", // +/- plus-minus                  "3.8 +/- 0.7"
	H11550: "±", //     "0.28%/d +/- 0.21%/d"
	H11003: "×", // *   multiplication              "0.07 x 10^9/L"
	H11547: "×", //     "x 10^12/L"
	H11001: "+", // +   plus                         "CD20+", "beta+-thal"
	H11091: "+", //
	H11545: "+", //
	H11002: "-", // -   minus                        "-5.1 to 27"
	H11546: "-", //     "(-2.6 +/- 0.3)"
	H11011: "~", // ~   approximately                "~500-750 kcal", "~20%"
	// --- Greek letters ---
	H9262: "µ", // micro sign                      "120 ug b.i.d."
	H9252: "β", // beta                            "37-amino acid beta-cell"
	H9251: "α", // alpha                           "the alpha level was"
	H9253: "γ", // gamma                           "Ggamma and Agamma globin"
	H9254: "δ", // delta                           "ornithine delta-aminotransferase"
	H9255: "ε", // epsilon                         "epsilon-globin"
	H9273: "χ", // chi                             "one-sided chi^2"
	H9274: "ψ", // psi
	// --- other ---
	H11034: "°", // degree                          "70°C until"
	H11032: "′", // prime                           "3' to it"
	H11557: "∞", // infinity                        "AUC0-inf"
	H11009: "∞", //
	H11569: "*", // asterisk (footnote marker)      "CR/CRu*"
	H23041: "®", // registered                      "PK-Analyst(R)"
};

/**
 * Resolve a PDF glyph name to the Unicode character it represents.
 * @param {string} glyphName - glyph name as it appears in /Differences, with or
 *   without a leading "/".
 * @returns {string | null} the recovered character, or null if unknown.
 */
export function resolveMathPiGlyph(glyphName) {
	if (!glyphName) return null;
	const name = glyphName[0] === "/" ? glyphName.slice(1) : glyphName;
	return Object.prototype.hasOwnProperty.call(MATH_PI_GLYPH_MAP, name)
		? MATH_PI_GLYPH_MAP[name]
		: null;
}

export { MATH_PI_GLYPH_MAP };
